import { useEffect, useRef, useState, type FormEvent } from 'react'
import {
  ApiError,
  api,
  can,
  readIf,
  refusalReason,
  tierLabel,
  tierRuleName,
  type Contact,
  type InviteResult,
  type MessagingPolicy,
  type TierIssuing,
  type Vendor,
} from '../lib/api'
import { ErrorBox, Loading } from '../components/DataTable'
import { InviteSent } from '../components/InviteSent'
import { VendorPicker } from '../components/VendorPicker'
import { DateRuleNote } from '../components/DateRuleNote'
import { Icon } from '../components/ui'
import { mailWent } from '../lib/mail'
import { myWorkChanged } from '../lib/myWork'
import { rememberSent } from '../lib/sentNote'
import { finishAt } from '../lib/finish'
import { taskOpenedFrom } from '../lib/origin'
import { TASK_VERB } from '../lib/status'
import { checkDueAfterToday, formatDay, todayIso, useLeftFields } from '../lib/dateRules'
import { openRequestDrawer } from '../lib/requestDrawer'
import { dueDiligenceRefusal, needsReason, wayForward } from '../lib/requestRules'
import {
  SEND_STEPS,
  dropDraft,
  pushedState,
  reachableStep,
  readDraft,
  reminderWords,
  sendAddress,
  shownStep,
  stepDepth,
  stepIndex,
  stepOut,
  stepProblem,
  stepTakesFocus,
  writeDraft,
  type SendAnswers,
  type SendFacts,
} from '../lib/sendSteps'
import '../components/assess/assess.css'

// Send questionnaire in three steps, opened from the Assessments list
// (release 3.3, O1 of the 7 Oct plan). The rules, the address and the history
// are lib/sendSteps.ts; this file draws them.
//
// # THE SAME SEND, NOT A SECOND ONE
//
// The send is api.inviteVendor, the call the vendor's own send screen makes
// (views/VendorInvite.tsx), and every refusal of it is handled the same way:
// the due diligence gate before the form with the way forward (approve the
// request, wait for the lead, open the approval), a reason for a suspended
// vendor, a contact added in place when nobody answers for the vendor, the
// answer-by date checked on the field (D11), and the server's refusal shown
// in its own words when it still says no. A send that goes lands on the
// vendor with the confirmation, as from the vendor's screen; one that does
// not go (mail off, held for a second person) says so here. A bulk send's
// approval is not shown here: it is a decision in My work only.

type Loaded = {
  vendorId: string
  vendor: Vendor
  contacts: Contact[]
  questionnaires: { code: string; name: string }[]
  tierQuestionnaire: string
  issuing?: TierIssuing
}

type Refusal = { code: string; message: string; remedy?: string; reference?: string }

function plusDays(n: number): string {
  const d = new Date()
  d.setDate(d.getDate() + n)
  return todayIso(d)
}

function vendorName(v: Vendor): string {
  return v.displayName?.trim() || v.legalName
}

function query(hash: string): URLSearchParams {
  const i = hash.indexOf('?')
  return new URLSearchParams(i < 0 ? '' : hash.slice(i + 1))
}

export function SendQuestionnaire() {
  // The address is read on every move: the steps are pushed here (no event)
  // and Back and Forward raise one.
  const [hash, setHash] = useState(() => (typeof window === 'undefined' ? '' : window.location.hash))
  useEffect(() => {
    const sync = () => setHash(window.location.hash)
    window.addEventListener('hashchange', sync)
    window.addEventListener('popstate', sync)
    return () => {
      window.removeEventListener('hashchange', sync)
      window.removeEventListener('popstate', sync)
    }
  }, [])
  const params = query(hash)
  const vendorId = params.get('vendor') ?? ''
  const asked = stepIndex(params.get('step'))

  // The answers kept for this vendor in this tab, if any (a reload).
  const [questionnaire, setQuestionnaire] = useState(() => readDraft(vendorId)?.questionnaire ?? '')
  const [dueOn, setDueOn] = useState(() => readDraft(vendorId)?.dueOn || plusDays(14))
  const [reason, setReason] = useState(() => readDraft(vendorId)?.reason ?? '')
  const [data, setData] = useState<Loaded | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [policy, setPolicy] = useState<MessagingPolicy | null>(null)
  const [reload, setReload] = useState(0)
  const [busy, setBusy] = useState(false)
  const [leaving, setLeaving] = useState(false)
  const [sent, setSent] = useState<InviteResult | null>(null)
  const [refusal, setRefusal] = useState<Refusal | null>(null)
  const [contact, setContact] = useState({ fullName: '', email: '' })
  const [contactBusy, setContactBusy] = useState(false)
  const leftDue = useLeftFields()
  const today = todayIso()

  // A vendor changed by the address (a link, Back to another send) brings its
  // own kept answers: one vendor's reason and date are never sent with another.
  const lastVendor = useRef(vendorId)
  useEffect(() => {
    if (lastVendor.current === vendorId) return
    lastVendor.current = vendorId
    const d = readDraft(vendorId)
    setQuestionnaire(d?.questionnaire ?? '')
    setDueOn(d?.dueOn || plusDays(14))
    setReason(d?.reason ?? '')
    setRefusal(null)
    setContact({ fullName: '', email: '' })
  }, [vendorId])

  // The vendor's record, its contacts, the published questionnaires and the
  // tier's own questionnaire: the reads the vendor's send screen makes.
  useEffect(() => {
    if (!vendorId) {
      setData(null)
      return
    }
    let live = true
    setError(null)
    Promise.all([
      api.vendor(vendorId),
      api.contacts(vendorId).catch(() => ({ contacts: [] as Contact[], count: 0, reachable: 0, note: '' })),
      api.questionnaires().catch(() => ({ questionnaires: [] })),
      api.tierProfiles().catch(() => ({ tierProfiles: [], note: '', issuing: undefined })),
    ])
      .then(([v, c, q, tp]) => {
        if (!live) return
        const profiles = (tp.tierProfiles ?? []) as { tier?: number; questionnaireCode?: string | null }[]
        const mine = profiles.find((p) => p.tier === v.inherentTier)
        // Only a questionnaire with a published version can be sent.
        const published = (q.questionnaires ?? []).filter((x) => x.publishedVersionId).map((x) => ({ code: x.code, name: x.name }))
        const tierQ = published.some((x) => x.code === mine?.questionnaireCode) ? (mine?.questionnaireCode ?? '') : ''
        setData({ vendorId, vendor: v, contacts: c.contacts ?? [], questionnaires: published, tierQuestionnaire: tierQ, issuing: tp.issuing })
        // A kept choice that is no longer published falls back to the tier's.
        setQuestionnaire((cur) => (published.some((x) => x.code === cur) ? cur : tierQ || (published[0]?.code ?? '')))
      })
      .catch((err) => {
        if (live) setError(err)
      })
    return () => {
      live = false
    }
  }, [vendorId, reload])

  // The workspace's reminder schedule, for a reader who may read it.
  useEffect(() => {
    let live = true
    readIf('config.read', () => api.messagingPolicy().then((r) => r.policy), null)
      .then((p) => {
        if (live) setPolicy(p)
      })
      .catch(() => undefined)
    return () => {
      live = false
    }
  }, [])

  // The answers are kept for this tab while the steps are walked.
  useEffect(() => {
    if (vendorId && !leaving && !sent) writeDraft(vendorId, { questionnaire, dueOn, reason })
  }, [vendorId, questionnaire, dueOn, reason, leaving, sent])

  const loaded = data && data.vendorId === vendorId ? data : null
  const v = loaded?.vendor ?? null
  const recipient = loaded ? (loaded.contacts.find((c) => c.wouldBeInvited) ?? loaded.contacts.find((c) => c.isActive && c.email)) : undefined
  const answers: SendAnswers = { vendorId, questionnaire, dueOn, reason }
  const facts: SendFacts = { vendor: v, recipient: Boolean(recipient), questionnaires: loaded?.questionnaires.length ?? 0 }
  const step = shownStep(asked, answers, facts, today)
  const furthest = reachableStep(answers, facts, today)

  // A new step takes the focus to its form (named by the step), so a press of
  // Enter that moves on never leaves the focus on the page's body. Not the
  // step a reload lands on: see stepTakesFocus.
  const formRef = useRef<HTMLFormElement>(null)
  const ready = loaded != null || !vendorId
  const drawnStep = useRef<number | null>(null)
  useEffect(() => {
    if (!ready) return
    const move = stepTakesFocus(drawnStep.current, step, ready)
    drawnStep.current = step
    if (move) formRef.current?.focus()
  }, [step, ready])

  // An address naming a step whose earlier step still has a problem opens
  // that earlier step, and the address says so too.
  useEffect(() => {
    if (!loaded || leaving || sent || step >= asked) return
    try {
      history.replaceState(history.state, '', sendAddress(step, vendorId))
      setHash(window.location.hash)
    } catch {
      // The step drawn is still the right one.
    }
  }, [loaded, step, asked, vendorId, leaving, sent])

  if (!can('questionnaire.issue')) {
    return (
      <div className="p1-page p1-wizard">
        <div className="p1-head">
          <div>
            <h1>{TASK_VERB.send}</h1>
          </div>
        </div>
        <div className="panel">
          <div className="note stop" role="status">
            <strong>Not part of your role</strong>
            <p style={{ margin: '4px 0 0' }}>{refusalReason('questionnaire.issue')}</p>
          </div>
        </div>
      </div>
    )
  }

  if (sent && v) return <InviteSent sent={sent} vendorName={vendorName(v)} vendorId={vendorId} />
  if (leaving) return <Loading what="the questionnaire" />

  // One history entry per step: Back steps back with the answers kept.
  function go(n: number) {
    try {
      history.pushState(pushedState(history.state), '', sendAddress(n, vendorId))
    } catch {
      window.location.hash = sendAddress(n, vendorId)
    }
    setHash(window.location.hash)
    setRefusal((r) => (r?.code === 'NO_CONTACT' ? r : null))
    window.scrollTo?.({ top: 0 })
  }

  function back() {
    if (stepDepth(history.state) > 0) {
      history.back()
      return
    }
    try {
      history.replaceState(history.state, '', sendAddress(step - 1, vendorId))
    } catch {
      window.location.hash = sendAddress(step - 1, vendorId)
    }
    setHash(window.location.hash)
  }

  // Choosing a vendor is an answer, not a step: it replaces the address.
  function pick(id: string) {
    const d = readDraft(id)
    setQuestionnaire(d?.questionnaire ?? '')
    setDueOn(d?.dueOn || plusDays(14))
    setReason(d?.reason ?? '')
    setRefusal(null)
    setContact({ fullName: '', email: '' })
    try {
      history.replaceState(history.state, '', sendAddress(0, id))
    } catch {
      window.location.hash = sendAddress(0, id)
    }
    setHash(window.location.hash)
  }

  // Leaves the steps this page pushed, then moves on (B9).
  async function leave(to: string) {
    setLeaving(true)
    await stepOut(stepDepth(history.state))
    finishAt(to)
  }

  async function cancel() {
    if (vendorId) dropDraft(vendorId)
    await leave(taskOpenedFrom() || '#/assessments')
  }

  async function send() {
    if (busy) return
    setBusy(true)
    setRefusal(null)
    try {
      const r = await api.inviteVendor(vendorId, { questionnaireCode: questionnaire || undefined, dueOn, reason: reason.trim() || undefined })
      dropDraft(vendorId)
      // A send that went lands on the vendor with the confirmation at the
      // top, as from the vendor's own send screen. One that did not go says
      // why here, with the steps behind it gone.
      if (r.status === 'ISSUED' && mailWent(r)) {
        rememberSent(vendorId, r)
        myWorkChanged()
        await leave('#/vendor/' + vendorId)
        return
      }
      setLeaving(true)
      await stepOut(stepDepth(history.state))
      setSent(r)
      setLeaving(false)
    } catch (err) {
      if (err instanceof ApiError) {
        const body = (err.body ?? {}) as { message?: string; remedy?: string; reference?: string }
        setRefusal({ code: err.code, message: body.message || err.message, remedy: body.remedy || err.sentRemedy, reference: body.reference })
      } else {
        setRefusal({ code: 'UNKNOWN', message: err instanceof Error ? err.message : String(err) })
      }
    } finally {
      setBusy(false)
    }
  }

  async function addContact() {
    setContactBusy(true)
    try {
      await api.createContact(vendorId, { role: 'PRIMARY', fullName: contact.fullName.trim(), email: contact.email.trim(), purpose: 'ASSESSMENT' })
      setRefusal(null)
      setReload((n) => n + 1)
    } catch (err) {
      setRefusal({ code: 'CONTACT', message: err instanceof ApiError ? err.issues.map((i) => i.detail).join(' ') || err.message : String(err) })
    } finally {
      setContactBusy(false)
    }
  }

  function next(ev: FormEvent) {
    ev.preventDefault()
    if (step === SEND_STEPS.length - 1) {
      void send()
      return
    }
    if (step === 1 && stepProblem(1, answers, facts, today)) {
      leftDue.leave('dueOn')
      return
    }
    if (stepProblem(step, answers, facts, today)) return
    go(step + 1)
  }

  const problem = stepProblem(step, answers, facts, today)
  const askReason = v ? needsReason(v) : false
  const gate = v && !askReason ? dueDiligenceRefusal(v) : null
  const way = v && gate ? wayForward({ ...v, id: vendorId }, gate, can('vendor.request_approve')) : null
  const qName = loaded?.questionnaires.find((x) => x.code === questionnaire)?.name ?? questionnaire
  const tierText = v ? v.tier?.label || (v.inherentTier ? tierLabel(v.inherentTier) : 'Not yet classified') : ''
  const rule = v?.tier?.recorded ? tierRuleName(v.tier.why, v.tier.ruleKey) : ''
  // Whether a second person approves this send before it goes, as the API
  // enforces it (TierIssuing). The approval itself is in their My work.
  const second =
    loaded?.issuing != null &&
    (loaded.issuing.singleInviteNeedsApprover || (v?.inherentTier != null && (loaded.issuing.tiersNeedingSecondPerson ?? []).includes(v.inherentTier)))
  const dueRule = checkDueAfterToday('Answer-by date', dueOn, today)
  const contactForm = v && !gate && (!recipient || refusal?.code === 'NO_CONTACT')
  const reminders = reminderWords(policy)
  const last = step === SEND_STEPS.length - 1

  return (
    <div className="p1-page p1-wizard">
      <div className="p1-head">
        <div>
          <h1>{TASK_VERB.send}</h1>
          {v && (
            <p>
              {vendorName(v)}, {v.vendorCode}
            </p>
          )}
        </div>
      </div>
      <StepBar at={step} furthest={furthest} onGo={go} />

      <form ref={formRef} tabIndex={-1} className="panel p1-form" onSubmit={next} aria-label={SEND_STEPS[step]?.label}>
        {step === 0 && (
          <>
            <div className="wide">
              <VendorPicker id="sq-vendor" label="Vendor" value={vendorId} onChange={pick} placeholder="Search by name or code" />
            </div>
            {vendorId && error != null && (
              <div className="wide">
                <ErrorBox error={error} what="This vendor" />
              </div>
            )}
            {vendorId && !loaded && error == null && (
              <div className="wide">
                <Loading what="the vendor" />
              </div>
            )}
            {gate && (
              <div className="wide note stop" role="status">
                <strong>Not yet</strong>
                <p style={{ margin: '4px 0 0' }}>{gate.message}</p>
                {way?.kind === 'wait' && (
                  <p className="muted" style={{ margin: '4px 0 0' }}>
                    Waiting on {way.who}. They see it on the vendor and in their notices.
                  </p>
                )}
                {way?.kind === 'approve-request' && (
                  <p style={{ margin: '6px 0 0' }}>
                    <a className="button" href={'#/vendor/' + vendorId} onClick={() => openRequestDrawer(vendorId)}>
                      Approve request
                    </a>
                  </p>
                )}
                {way?.kind === 'open' && (
                  <p style={{ margin: '6px 0 0' }}>
                    <a className="button" href={way.href}>
                      {way.label}
                    </a>
                  </p>
                )}
              </div>
            )}
            {v && !gate && (
              <>
                <div>
                  <label htmlFor="sq-q">Questionnaire</label>
                  <select id="sq-q" value={questionnaire} onChange={(e) => setQuestionnaire(e.target.value)}>
                    {loaded?.questionnaires.map((q) => (
                      <option key={q.code} value={q.code}>
                        {q.name} {q.code === loaded.tierQuestionnaire ? '(set for this tier)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <span className="sq-label">Goes to</span>
                  <p className="sq-value">
                    {recipient ? (
                      <>
                        {recipient.fullName || recipient.email} <span className="muted">{recipient.email}</span>
                      </>
                    ) : (
                      <span className="muted">Nobody yet</span>
                    )}
                  </p>
                </div>
                {askReason && (
                  <div className="wide">
                    <label htmlFor="sq-reason">Reason</label>
                    <textarea id="sq-reason" rows={2} maxLength={1000} value={reason} aria-describedby="sq-reason-note" onChange={(e) => setReason(e.target.value)} />
                    <p className="sw-help" id="sq-reason-note">
                      This vendor is suspended. Say why it is sent due diligence; the reason goes on its record.
                    </p>
                  </div>
                )}
              </>
            )}
            {contactForm && (
              <div className="wide panel" style={{ background: 'var(--surface-2)' }}>
                <strong>Add the person who answers for {vendorName(v)}</strong>
                <div className="p1-form" style={{ marginTop: 8 }}>
                  <div>
                    <label htmlFor="sq-cname">Name</label>
                    <input id="sq-cname" type="text" value={contact.fullName} onChange={(e) => setContact({ ...contact, fullName: e.target.value })} />
                  </div>
                  <div>
                    <label htmlFor="sq-cemail">Email</label>
                    <input id="sq-cemail" type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} />
                  </div>
                  <div className="wide">
                    <button type="button" disabled={contactBusy || !contact.email} onClick={() => void addContact()}>
                      {contactBusy ? 'Saving' : 'Save contact'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {step === 1 && (
          <>
            <div>
              <label htmlFor="sq-due">Answer by</label>
              <input
                id="sq-due"
                type="date"
                value={dueOn}
                min={plusDays(1)}
                onChange={(e) => setDueOn(e.target.value)}
                onBlur={() => leftDue.leave('dueOn')}
                aria-invalid={leftDue.shown('dueOn', dueRule) ? true : undefined}
                aria-describedby={leftDue.shown('dueOn', dueRule) ? 'sq-due-rule' : undefined}
              />
              <DateRuleNote id="sq-due-rule" message={leftDue.shown('dueOn', dueRule)} />
            </div>
            <div>
              <span className="sq-label">Reminders</span>
              <p className="sq-value">{reminders}</p>
            </div>
          </>
        )}

        {last && v && (
          <div className="wide">
            <dl className="kv">
              <dt>Vendor</dt>
              <dd>
                {vendorName(v)} <span className="muted">{v.vendorCode}</span>
              </dd>
              <dt>Inherent tier</dt>
              <dd>
                {tierText}
                {rule ? <span className="muted"> - {rule}</span> : null}
              </dd>
              <dt>Goes to</dt>
              <dd>{recipient ? recipient.fullName || recipient.email : <span className="muted">Nobody yet</span>}</dd>
              <dt>Questionnaire</dt>
              <dd>{qName}</dd>
              <dt>Answer by</dt>
              <dd>{formatDay(dueOn)}</dd>
              <dt>Reminders</dt>
              <dd>{reminders}</dd>
              {reason.trim() !== '' && (
                <>
                  <dt>Reason</dt>
                  <dd>{reason.trim()}</dd>
                </>
              )}
            </dl>
            {second && <p className="muted sq-note">A second person approves it before it goes out.</p>}
          </div>
        )}

        {refusal && (
          <div className="wide note stop" role="alert">
            <strong>{refusal.code === 'ALREADY_OPEN' ? 'Already sent' : refusal.code === 'CONTACT' ? 'Contact not saved' : 'Not sent'}</strong>
            <p style={{ margin: '4px 0 0' }}>{refusal.message}</p>
            {refusal.remedy && (
              <p className="muted" style={{ margin: '4px 0 0' }}>
                {refusal.remedy}
              </p>
            )}
            {refusal.code === 'ALREADY_OPEN' && (
              <p style={{ margin: '6px 0 0' }}>
                <a href={'#/vendor/' + vendorId}>See the open questionnaire on the vendor</a>
              </p>
            )}
            {refusal.code === 'NO_CONTACT' && step > 0 && (
              <p style={{ margin: '6px 0 0' }}>
                <button type="button" className="linkish" onClick={() => go(0)}>
                  Add a contact
                </button>
              </p>
            )}
          </div>
        )}

        <div className="p1-form-foot">
          {step > 0 && (
            <button type="button" onClick={back} disabled={busy}>
              Back
            </button>
          )}
          <button type="submit" className="primary" disabled={busy || (step === 0 && problem !== '')}>
            {last ? (busy ? 'Sending' : TASK_VERB.send) : 'Next'}
          </button>
          <button type="button" onClick={() => void cancel()} disabled={busy}>
            Cancel
          </button>
          {step === 0 && problem !== '' && !gate && vendorId !== '' && loaded && <span className="muted">{problem}</span>}
        </div>
      </form>
    </div>
  )
}

// The row of steps. A step already reachable is a button that goes there
// (Back model B1: the step bar is the wizard's way back, beside Back in the
// footer); the step on screen and the ones not reachable yet are not.
export function StepBar({ at, furthest, onGo }: { at: number; furthest: number; onGo: (n: number) => void }) {
  return (
    <ol className="stepper sq-steps" aria-label="Steps">
      {SEND_STEPS.map((s, i) => {
        const inner = (
          <>
            <span className="n">{i < at ? <Icon name="check" size={12} /> : i + 1}</span> {s.label}
            {i < at && <span className="vh"> (done)</span>}
          </>
        )
        return (
          <li key={s.key} className={'st' + (i === at ? ' on' : i < at ? ' done' : '')} aria-current={i === at ? 'step' : undefined}>
            {i !== at && i <= furthest ? (
              <button type="button" className="sq-step-go" onClick={() => onGo(i)}>
                {inner}
              </button>
            ) : (
              inner
            )}
          </li>
        )
      })}
    </ol>
  )
}
